import { ElementNode, LexicalNode, NodeKey, EditorConfig, SerializedElementNode } from 'lexical';

type LegalConstraints = {
  keepWithNext?: boolean;
  noSplit?: boolean;
};

export type SerializedClauseNode = SerializedElementNode & {
  type: 'clause';
  version: 2;
  blockId: string;
  attrs?: { clauseId?: string; clauseTitle?: string; numberingPath?: string };
  legalConstraints?: LegalConstraints;
  lang?: string;
  dir?: 'ltr' | 'rtl' | 'auto';
};

export class ClauseNode extends ElementNode {
  __blockId: string;
  __attrs: SerializedClauseNode['attrs'];
  __legal: LegalConstraints | undefined;
  __lang: string | undefined;
  __dir: 'ltr' | 'rtl' | null; // ElementNode supports only ltr|rtl|null

  static getType(): string { return 'clause'; }
  static clone(node: ClauseNode): ClauseNode {
    const n = new ClauseNode(node.__blockId, node.__key);
    n.__attrs = node.__attrs ? { ...node.__attrs } : undefined;
    n.__legal = node.__legal ? { ...node.__legal } : undefined;
    n.__lang = node.__lang;
    n.__dir = node.__dir;
    return n;
  }

  constructor(blockId: string, key?: NodeKey) {
    super(key);
    this.__blockId = blockId;
    this.__attrs = undefined;
    this.__legal = undefined;
    this.__lang = undefined;
    this.__dir = null;
  }

  // Top-level block semantics
  isInline(): boolean { return false; }
  canBeEmpty(): boolean { return true; }
  canInsertTextBefore(): boolean { return false; }
  canInsertTextAfter(): boolean { return false; }

  createDOM(_config: EditorConfig): HTMLElement {
    const el = document.createElement('div');
    el.className = 'edg-clause';
    el.dataset.edgNode = 'clause';
    el.dataset.blockId = this.__blockId;
    if (this.__attrs?.clauseId) el.dataset.clauseId = this.__attrs.clauseId;
    if (this.__attrs?.numberingPath) el.dataset.numberingPath = this.__attrs.numberingPath;
    if (this.__lang) el.lang = this.__lang;
    if (this.__dir) el.dir = this.__dir;
    if (this.__legal?.keepWithNext) el.classList.add('edg-clause--keep-with-next');
    if (this.__legal?.noSplit) el.classList.add('edg-clause--no-split');
    return el;
  }

  updateDOM(prevNode: ClauseNode, dom: HTMLElement): boolean {
    const changed =
      prevNode.__blockId !== this.__blockId ||
      prevNode.__lang !== this.__lang ||
      prevNode.__dir !== this.__dir ||
      JSON.stringify(prevNode.__attrs) !== JSON.stringify(this.__attrs) ||
      JSON.stringify(prevNode.__legal) !== JSON.stringify(this.__legal);

    if (!changed) return false;

    dom.dataset.blockId = this.__blockId;
    if (this.__attrs?.clauseId) dom.dataset.clauseId = this.__attrs.clauseId; else delete (dom as any).dataset?.clauseId;
    if (this.__attrs?.numberingPath) dom.dataset.numberingPath = this.__attrs.numberingPath; else delete (dom as any).dataset?.numberingPath;
    if (this.__lang) dom.lang = this.__lang; else dom.removeAttribute('lang');
    if (this.__dir) dom.dir = this.__dir; else dom.removeAttribute('dir');
    dom.classList.toggle('edg-clause--keep-with-next', !!this.__legal?.keepWithNext);
    dom.classList.toggle('edg-clause--no-split', !!this.__legal?.noSplit);
    return false;
  }

  static importJSON(j: SerializedClauseNode): ClauseNode {
    const node = new ClauseNode(j.blockId);
    node.__attrs = j.attrs ? { ...j.attrs } : undefined;
    node.__legal = j.legalConstraints ? { ...j.legalConstraints } : undefined;
    node.__lang = j.lang;
    node.__dir = (j.dir === 'ltr' || j.dir === 'rtl') ? j.dir : null;
    node.setFormat(j.format);
    node.setIndent(j.indent);
    return node;
  }

  exportJSON(): SerializedClauseNode {
    return {
      ...super.exportJSON(),
      type: 'clause',
      version: 2,
      blockId: this.__blockId,
      attrs: this.__attrs ? { ...this.__attrs } : undefined,
      legalConstraints: this.__legal ? { ...this.__legal } : undefined,
      lang: this.__lang,
      dir: this.__dir,
    } as SerializedClauseNode;
  }

  static importDOM() {
    return {
      div: (domNode: Node) => {
        if (!(domNode instanceof HTMLElement)) return null;
        if (domNode.dataset?.edgNode === 'clause' && domNode.dataset.blockId) {
          return {
            conversion: () => {
              const node = new ClauseNode(domNode.dataset.blockId!);
              if (domNode.dataset.clauseId) node.setAttrs({ clauseId: domNode.dataset.clauseId });
              if (domNode.lang) node.setLang(domNode.lang);
              if (domNode.dir) node.setDir(domNode.dir as any);
              return { node };
            },
            priority: 2,
          } as any;
        }
        return null;
      },
    } as any;
  }

  exportDOM(): { element: HTMLElement } {
    const element = this.createDOM({} as EditorConfig);
    return { element };
  }

  // Best-effort Enter policy (signature not enforced strictly by types across versions)
  insertNewAfter(_selection: any) {
    const newNode = new ClauseNode((globalThis.crypto?.randomUUID && crypto.randomUUID()) || `clause_${Date.now()}`);
    return { node: newNode, selection: null } as any;
  }

  // Mutators (editor.update)
  setAttrs(next: SerializedClauseNode['attrs'] | undefined) { const self = this.getWritable(); (self as any).__attrs = next ? { ...next } : undefined; }
  setLegalConstraints(next: LegalConstraints | undefined) { const self = this.getWritable(); (self as any).__legal = next ? { ...next } : undefined; }
  setLang(lang?: string) { const self = this.getWritable(); (self as any).__lang = lang ?? undefined; }
  setDir(dir?: 'ltr' | 'rtl' | 'auto') { const self = this.getWritable(); (self as any).__dir = (dir === 'ltr' || dir === 'rtl') ? dir : null; }

  get blockId() { return (this.getLatest() as any).__blockId; }
  get attrs() { return (this.getLatest() as any).__attrs; }
  get legal() { return (this.getLatest() as any).__legal; }
}

export function $createClauseNode(init?: {
  blockId?: string;
  attrs?: SerializedClauseNode['attrs'];
  legalConstraints?: LegalConstraints;
  lang?: string;
  dir?: 'ltr' | 'rtl' | 'auto';
}): ClauseNode {
  const n = new ClauseNode(init?.blockId ?? ((globalThis.crypto?.randomUUID && crypto.randomUUID()) || `clause_${Date.now()}`));
  if (init?.attrs) n.setAttrs(init.attrs);
  if (init?.legalConstraints) n.setLegalConstraints(init.legalConstraints);
  if (init?.lang) n.setLang(init.lang);
  if (init?.dir) n.setDir(init.dir);
  return n;
}

export function $isClauseNode(node?: LexicalNode | null): node is ClauseNode { return node instanceof ClauseNode; }


